import { toUserFacingError } from '../lib/user-facing-error';
import { classifyInterviewWebSocketError } from './interviewWebSocketProtocol';

export interface InterviewWebSocketErrorPayload {
  code?: unknown;
  message?: unknown;
}

export interface InterviewWebSocketErrorControllerDependencies {
  resetCommonUi: () => void;
  setSessionReady: (ready: boolean) => void;
  invalidateSession: (reason: string) => void;
  requestSessionBindingRefresh: (reason: string) => void;
  resetTranscriptState: () => void;
  isRecordingOrStopping: () => boolean;
  resetRecordingAfterSessionMissing: () => void | Promise<void>;
  stopSilenceDetection: () => void;
  onInterviewClosedByScoring: () => void;
  toast: (opts: {
    title: string;
    description: string;
    variant?: "default" | "destructive" | null | undefined;
  }) => void;
  logError: (code: unknown, message: unknown) => void;
  logResetError: (error: unknown) => void;
}

/** WebSocketのerrorメッセージを復旧・採点中・一般エラーへ分類してUIへ反映する。 */
export class InterviewWebSocketErrorController {
  constructor(private readonly dependencies: InterviewWebSocketErrorControllerDependencies) {}

  handle(payload: InterviewWebSocketErrorPayload = {}): void {
    const { code, message } = payload;
    this.dependencies.logError(code, message);
    this.dependencies.resetCommonUi();

    const errorKind = classifyInterviewWebSocketError(code, message);
    if (errorKind === 'session_recovery') {
      this.handleSessionRecovery();
      return;
    }

    if (errorKind === 'interview_finished') {
      this.dependencies.onInterviewClosedByScoring();
      this.dependencies.toast({
        title: 'この面接は採点中です',
        description: typeof message === 'string' && message.trim()
          ? message
          : 'この面接では追加の録音を受け付けていません。',
        variant: 'destructive',
      });
      return;
    }

    const userFacingError = toUserFacingError({ code, message }, 'conversation');
    this.dependencies.toast({ ...userFacingError, variant: 'destructive' });
  }

  private handleSessionRecovery(): void {
    this.dependencies.setSessionReady(false);
    this.dependencies.invalidateSession('recoverable_session_error');
    this.dependencies.requestSessionBindingRefresh('recoverable_session_error');
    this.dependencies.resetTranscriptState();

    if (this.dependencies.isRecordingOrStopping()) {
      void Promise.resolve(this.dependencies.resetRecordingAfterSessionMissing())
        .catch((error) => this.dependencies.logResetError(error));
    } else {
      this.dependencies.stopSilenceDetection();
    }

    this.dependencies.toast({
      title: '録音をリセットしました',
      description: '面接セッションを復旧しています。つながったら、もう一度マイクを押して話してください。',
      variant: 'destructive',
    });
  }
}
